// Global
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { useTheme } from 'lib/context/ThemeContext';
import { useEffect, useState } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';

// Components
import { ComponentProps } from 'lib/types/component-props';
import { getEnum } from 'lib/utils/get-enum';
import { getHeadingLevel } from 'lib/utils/getHeadingLevel';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import ButtonGroup from 'src/helpers/ButtonGroup/ButtonGroup';
import {
  ComponentPadding,
  GetCompositeComponentSpacingClass,
} from 'src/helpers/Component/Component';
import { EnumField } from 'lib/utils/get-enum';
import { Eyebrow } from 'src/helpers/Eyebrow';
import Headline from 'src/helpers/Headline/Headline';
import { MediaPrimary } from 'src/helpers/Media';
import RichTextWrapper from 'src/helpers/RichTextWrapper/RichTextWrapper';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import { HeroMediaBackgroundTheme } from './HeroMediaBackground.theme';
import { cta1ToButtonProps, cta2ToButtonProps } from 'src/helpers/Button';

export type HeroMediaBackgroundProps =
  Feature.EnterpriseWeb.Enterprise.Components.Hero.HeroMediaBackground & ComponentProps;
const HeroMediaBackground = (props: HeroMediaBackgroundProps) => {
  const { fields } = props;

  const { themeName, themeData } = useTheme(HeroMediaBackgroundTheme);
  const { screenType, currentScreenWidth } = useCurrentScreenType();
  const [stepFixed, setStepFixed] = useState('');

  useEffect(() => {
    const raqBanner = document.getElementById('raqbanner');
    if (raqBanner && currentScreenWidth <= 1007) {
      setStepFixed('mt-[123px]');
    } else {
      setStepFixed('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreenWidth]);

  const [isVideoPaused, setIsVideoPaused] = useState(
    !fields?.primaryVideo?.fields.youTubeAutoPlay?.value || false
  );

  if (!fields) {
    return null;
  }

  const isDesktop = screenType !== 'sm';
  let hasOverlay = !!getEnum(fields.overlay);

  let fontColorClass = '';

  if (hasOverlay) {
    fontColorClass = 'md:text-white';
  } else {
    switch (getEnum(fields.fontColor)) {
      case 'gray':
        fontColorClass = 'md:text-dark-gray';
        break;
      case 'white':
        fontColorClass = 'md:text-white';
        break;
      default:
        fontColorClass = 'md:text-black';
        break;
    }
  }

  if (themeName === 'rba') {
    if (fields.overlay === null) {
      hasOverlay = true;
    }
  }

  const togglePlayPause = () => {
    const videoId = (
      props.fields?.primaryVideo as Feature.EnterpriseWeb.Enterprise.Elements.Media.YouTubeVideo
    )?.fields.videoId.value;

    const iframeContentWindow = (document.getElementById(videoId) as HTMLIFrameElement)
      ?.contentWindow;

    if (iframeContentWindow) {
      iframeContentWindow.postMessage(
        `{"event":"command", "func":"${isVideoPaused ? 'playVideo' : 'pauseVideo'}", "args":""}`,
        '*'
      );

      setIsVideoPaused(!isVideoPaused);
    }
  };

  const componentSpacing = GetCompositeComponentSpacingClass(
    props?.fields?.componentSpacing,
    props?.fields?.componentPadding,
    props?.fields?.componentMargin,
    'undefined'
  );

  const getHasPaddingInValue = (field: EnumField<ComponentPadding>) => {
    return getEnum<ComponentPadding>(field)?.toLowerCase().indexOf('padding') !== -1;
  };

  const componentPadding = GetCompositeComponentSpacingClass(
    getHasPaddingInValue(props?.fields?.componentSpacing)
      ? props?.fields?.componentSpacing
      : 'undefined',
    getHasPaddingInValue(props?.fields?.componentPadding)
      ? props?.fields?.componentPadding
      : 'undefined',
    getHasPaddingInValue(props?.fields?.componentMargin)
      ? props?.fields?.componentMargin
      : 'undefined',
    'undefined'
  );

  return (
    <div
      data-component="hero/heromediabackground"
      className={classNames('relative', componentSpacing, stepFixed)}
    >
      <div className={themeData.classes.mediaContainer}>
        <MediaPrimary imageLayout="responsive" ratio="auto" priority {...props} />
        {fields.primaryVideo && (
          <div
            className={classNames(
              'absolute right-0 bottom-0 z-10 cursor-pointer rounded-full bg-black',
              themeData.classes.iconWrapper
            )}
            onClick={togglePlayPause}
          >
            {isVideoPaused ? <SvgIcon icon="play" size="40" /> : <SvgIcon icon="pause" size="40" />}
          </div>
        )}
      </div>
      {hasOverlay && isDesktop && (
        <div
          className={classNames(
            themeData.classes.overlay,
            themeName == 'aw' ? componentPadding : ''
          )}
        ></div>
      )}
      <div className={classNames(themeData.classes.contentWrapper, fontColorClass)}>
        <div className={classNames(themeData.classes.contentContainer, fontColorClass)}>
          <Eyebrow classes={classNames(themeData.classes.eyebrow, fontColorClass)} {...props} />
          <Headline
            useTag={getHeadingLevel('h1', fields?.headlineLevel)}
            classes={classNames(themeData.classes.headline, fontColorClass)}
            {...props}
          />
          <RichTextWrapper
            classes={classNames(themeData.classes.bodyClass, fontColorClass)}
            field={props.fields?.body}
          />
          {fields.cta1Link && (
            <ButtonGroup
              cta1={cta1ToButtonProps(props, themeData.classes.buttonGroupClass.cta1Classes)}
              cta2={cta2ToButtonProps(props, themeData.classes.buttonGroupClass.cta2Classes)}
              wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default withDatasourceCheck()<HeroMediaBackgroundProps>(HeroMediaBackground);
